import clsx from 'clsx'
import { GetStaticPropsContext } from 'next'
import { SWRConfig } from 'swr'
import { BaseDataType, MagazinePost } from '../@types/statamic'
import { PageProps } from '../@types/util'
import BaseLayout from '../components/elements/BaseLayout'
import CookieBanner from '../components/elements/CookieBanner'
import FaqJsonLd from '../components/elements/FaqJsonLd'
import JsonLd from '../components/elements/JsonLd'
import Picture from '../components/elements/Picture'
import Container from '../components/layout/Container'
import BaseData from '../graphql/BaseData.graphql'
import DefaultPageQuery from '../graphql/DefaultPageQuery.graphql'
import { filterNavs } from '../lib/filterNavs'
import {
  getBlockDescription,
  getBlockImage,
  getBlockTitle,
  loadBlockProps,
  renderBlock,
} from '../lib/PageBuilder'
import { batchRequests, statamicConfig } from '../StatamicGraphQLClient'
import { PageParams, PreviewData } from '@/src/@types/infrastructur'
import BreadcrumbListJsonLd from '@/src/components/elements/BreadcrumbListJsonLd'
import MetaTags from '@/src/components/MetaTags'
import siteInfo from '@/src/lib/siteInfo'
import { ensureTrailingSlash } from '@/src/lib/str'

const BASE_URL = process.env.BASE_URL

type Props = PageProps<typeof getStaticProps>

export async function getStaticProps(
  context: GetStaticPropsContext<PageParams, PreviewData>
) {
  const urlParts = [...(context.params?.uri || [])]
  const currentUri = `/${urlParts.join('/')}`

  const [{ entry }, { navs, settings, globalCatcher }] = await batchRequests<
    [MagazinePost, BaseDataType]
  >(
    [
      {
        document: DefaultPageQuery,
        variables: {
          uri: currentUri,
          locale: `${process.env.NEXT_PUBLIC_STATAMIC_SITE_HANDLE}_de`,
          collections: statamicConfig.collections,
        },
      },
      {
        document: BaseData,
        variables: {
          locale: `${process.env.NEXT_PUBLIC_STATAMIC_SITE_HANDLE}_de`,
          settingsHandle: statamicConfig.settingsHandle,
          catcherHandle: statamicConfig.catcherHandle,
        },
      },
    ],
    context.previewData?.statamicToken
  )

  const filteredNavs = filterNavs(navs)
  const blocks = await loadBlockProps(entry.page_builder, context)

  if (!entry.social_title) {
    entry.social_title = entry.seo_title
  }
  if (!entry.social_text) {
    entry.social_text = entry.seo_summary
  }
  if (!entry.social_image) {
    entry.social_image = entry.image
  }

  if (
    !entry.social_title ||
    !entry.seo_summary ||
    !entry.social_text ||
    !entry.social_image
  ) {
    for (const block of blocks) {
      const title = getBlockTitle(block)
      if (title && !entry.social_title) {
        entry.social_title = title
      }

      const text = getBlockDescription(block)
      if (text && !entry.social_text) {
        entry.social_text = text
      }
      if (text && !entry.seo_summary) {
        entry.seo_summary = text
      }

      const image = getBlockImage(block)
      if (image && !entry.social_image) {
        entry.social_image = image
      }

      if (
        entry.social_title &&
        entry.seo_summary &&
        entry.social_text &&
        entry.social_image
      ) {
        break
      }
    }
  }

  const uri = context.params?.uri ? context.params?.uri?.join('/') : ''

  return {
    props: {
      blocks,
      navs: filteredNavs,
      settings,
      globalCatcher,
      title: entry.title,
      image: entry.image || null,
      isoDate: entry.isoDate,
      seoTitle: entry.seo_title || null,
      seoSummary: entry.seo_summary || null,
      socialTitle: entry.social_title || null,
      socialText: entry.social_text || null,
      socialImage: entry.social_image || null,
      isoModifiedDate: entry.isoModifiedDate,
      date: entry.date,
      show_date: entry.show_date,
      fullUrl: BASE_URL + '/' + ensureTrailingSlash(uri),
      baseUrl: BASE_URL,
      noindex: entry.noindex,
    },
  }
}
export default function Magazine({
  blocks,
  navs,
  title,
  image,
  isoDate,
  isoModifiedDate,
  settings,
  globalCatcher,
  seoTitle,
  seoSummary,
  socialTitle,
  socialText,
  socialImage,
  date,
  show_date,
  fullUrl,
  baseUrl,
  noindex,
}: Props) {
  return (
    <>
      <MetaTags
        seoTitle={seoTitle}
        seoSummary={seoSummary}
        title={socialTitle}
        description={socialText}
        fullUrl={fullUrl}
        date={date}
        image={socialImage}
        noindex={noindex}
      />
      <JsonLd
        data={{
          '@context': 'https://schema.org',
          '@type': 'NewsArticle',
          mainEntityOfPage: {
            '@type': 'WebPage',
            '@id': fullUrl,
          },
          headline: title,
          image: [image?.permalink],
          datePublished: isoDate,
          dateModified: isoModifiedDate,
          author: {
            '@type': 'Organization',
            name: siteInfo.orgName,
            url: baseUrl,
          },
          publisher: {
            '@type': 'Organization',
            name: siteInfo.orgName,
            logo: {
              '@type': 'ImageObject',
              url: settings.logo?.permalink,
            },
          },
        }}
      />
      <CookieBanner />
      <FaqJsonLd blocks={blocks} />
      <BreadcrumbListJsonLd
        items={[
          {
            name: 'Wissen',
            url: `${baseUrl}/wissen/`,
          },
          {
            name: title,
            url: fullUrl,
          },
        ]}
        baseUrl={baseUrl}
      />
      <BaseLayout navs={navs} settings={settings} globalCatcher={globalCatcher}>
        <div>
          <div className="h-16 w-full xl:h-24" />
          <Container>
            <header className="relative -z-20 h-[13.125rem] w-full lg:h-[35.5rem]">
              <Picture
                {...image}
                fill
                sizes="(min-width: 1024px) 90vw, 100vw"
                priority
                className="rounded-xl object-cover"
              />
            </header>
          </Container>
          <div className="relative mx-auto overflow-hidden pb-16 lg:-mt-40">
            {show_date && (
              <div className="mx-auto max-w-blog rounded-t-[15px] bg-white px-7 pt-7 pb-5">
                <time dateTime={isoDate} className="font-bold lg:text-xl">
                  {date}
                </time>
              </div>
            )}
            {!show_date && (
              <div className="mx-auto h-8 max-w-blog rounded-t-[15px] bg-white" />
            )}
            <div className="absolute inset-x-0 -z-20 mx-auto size-full h-64 max-w-blog bg-white px-7"></div>
            <div className="mx-auto max-w-blog px-1">
              {blocks.map((block, index) => (
                <SWRConfig
                  key={index}
                  value={{ fallback: block.fallback ?? {} }}
                >
                  <div
                    className={clsx({
                      'mt-20 md:mt-24':
                        index !== 0 &&
                        !(
                          blocks[index - 1].type === 'content' &&
                          block.type === 'content'
                        ),
                    })}
                  >
                    {renderBlock(block, index, blocks, {
                      settings,
                      socialImage,
                      fullUrl,
                      blueprint: 'magazine',
                      newSpacing: false,
                    })}
                  </div>
                </SWRConfig>
              ))}
            </div>
          </div>
        </div>
      </BaseLayout>
    </>
  )
}
